/**
 * IndexedDB persistence for saved RBF interpretation layers, keyed per project.
 *
 * Each layer stores: id, name, visible, opacity, color, and the geometry
 * position buffer (Float32Array) which is sufficient to reconstruct the mesh.
 */
import * as THREE from "three";

const DB_VERSION = 1;
const STORE_NAME = "layers";

export interface RBFLayerRecord {
  id: number;
  name: string;
  visible: boolean;
  opacity: number;
  color: string;
  /** Serialized position attribute (flat xyz triples) */
  positions: Float32Array;
}

export interface RBFLayer {
  id: number;
  name: string;
  visible: boolean;
  opacity: number;
  color: string;
  geometry: THREE.BufferGeometry;
}

function dbName(projectId: string): string {
  return `${projectId}:rbf-layers`;
}

function openDB(projectId: string): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(dbName(projectId), DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: "id" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function recordFromLayer(layer: RBFLayer): RBFLayerRecord {
  const posAttr = layer.geometry.getAttribute("position");
  const positions = new Float32Array(posAttr.array);
  return { id: layer.id, name: layer.name, visible: layer.visible, opacity: layer.opacity, color: layer.color, positions };
}

function layerFromRecord(record: RBFLayerRecord): RBFLayer {
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.BufferAttribute(record.positions, 3));
  geo.computeVertexNormals();
  return { id: record.id, name: record.name, visible: record.visible, opacity: record.opacity, color: record.color, geometry: geo };
}

export async function loadRBFLayers(projectId: string): Promise<RBFLayer[]> {
  try {
    const db = await openDB(projectId);
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readonly");
      const store = tx.objectStore(STORE_NAME);
      const req = store.getAll();
      req.onsuccess = () => {
        const records = req.result as RBFLayerRecord[];
        resolve(records.map(layerFromRecord));
      };
      req.onerror = () => reject(req.error);
    });
  } catch (err) {
    console.warn("[RBF Store] Failed to load layers:", err);
    return [];
  }
}

export async function saveRBFLayer(projectId: string, layer: RBFLayer): Promise<void> {
  const db = await openDB(projectId);
  const record = recordFromLayer(layer);
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    const store = tx.objectStore(STORE_NAME);
    const req = store.put(record);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

export async function deleteRBFLayer(projectId: string, layerId: number): Promise<void> {
  const db = await openDB(projectId);
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    const store = tx.objectStore(STORE_NAME);
    const req = store.delete(layerId);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

export async function updateRBFLayerVisibility(projectId: string, layerId: number, visible: boolean): Promise<void> {
  const db = await openDB(projectId);
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    const store = tx.objectStore(STORE_NAME);
    const getReq = store.get(layerId);
    getReq.onsuccess = () => {
      const record = getReq.result as RBFLayerRecord | undefined;
      if (!record) { resolve(); return; }
      record.visible = visible;
      const putReq = store.put(record);
      putReq.onsuccess = () => resolve();
      putReq.onerror = () => reject(putReq.error);
    };
    getReq.onerror = () => reject(getReq.error);
  });
}
